import React, { useEffect, useState } from "react";
import client from "@/lib/api";
import { ComparativeReport } from "@/lib/types";
import { money, compactMoney } from "@/lib/format";
import { PageHeader } from "@/components/shared";
import { Loader2, TrendingUp, TrendingDown, Minus } from "lucide-react";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  Legend,
  ResponsiveContainer,
  CartesianGrid,
} from "recharts";

const monthLabel = (ym: string) => {
  const [y, m] = ym.split("-");
  const names = ["Ene", "Feb", "Mar", "Abr", "May", "Jun", "Jul", "Ago", "Sep", "Oct", "Nov", "Dic"];
  return `${names[parseInt(m, 10) - 1]} ${y}`;
};

const ReportsPage: React.FC = () => {
  const [data, setData] = useState<ComparativeReport | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    client.get("/reports/comparative").then((r) => setData(r.data)).finally(() => setLoading(false));
  }, []);

  if (loading)
    return (
      <div className="flex h-96 items-center justify-center">
        <Loader2 className="h-6 w-6 animate-spin text-[#DD0000]" />
      </div>
    );
  if (!data) return null;

  const chartData = data.rows.map((r) => ({
    label: r.label,
    [data.previous_label]: r.previous,
    [data.current_label]: r.current,
  }));

  return (
    <div data-testid="reports-page">
      <PageHeader
        title="Reportes comparativos"
        subtitle={`${monthLabel(data.current_label)} vs ${monthLabel(data.previous_label)}`}
      />

      <div className="grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-4">
        {data.rows.map((r) => {
          const good = r.positive_up ? r.delta >= 0 : r.delta <= 0;
          const flat = r.delta === 0;
          const Icon = flat ? Minus : r.delta > 0 ? TrendingUp : TrendingDown;
          const color = flat ? "text-slate-400" : good ? "text-emerald-600" : "text-red-600";
          return (
            <div key={r.key} className="border border-slate-200 bg-white p-5" data-testid={`report-${r.key}`}>
              <div className="text-[10px] font-bold uppercase tracking-[0.13em] text-slate-500">{r.label}</div>
              <div className="mt-3 text-2xl font-medium tabular text-slate-900">{money(r.current)}</div>
              <div className="mt-1 flex items-center justify-between text-xs">
                <span className="text-slate-400">Ant: {compactMoney(r.previous)}</span>
                <span className={`flex items-center gap-1 font-medium ${color}`}>
                  <Icon className="h-3.5 w-3.5" strokeWidth={2} />
                  {r.pct > 0 ? "+" : ""}
                  {r.pct}%
                </span>
              </div>
            </div>
          );
        })}
      </div>

      <div className="mt-6 border border-slate-200 bg-white p-5" data-testid="reports-chart">
        <h3 className="mb-4 text-sm font-semibold tracking-tight text-slate-800">
          Comparativo de métricas · {monthLabel(data.previous_label)} vs {monthLabel(data.current_label)}
        </h3>
        <ResponsiveContainer width="100%" height={340}>
          <BarChart data={chartData} margin={{ bottom: 40 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" vertical={false} />
            <XAxis dataKey="label" tick={{ fontSize: 10, fill: "#475569" }} angle={-20} textAnchor="end" height={60} interval={0} />
            <YAxis tickFormatter={(v) => compactMoney(v)} tick={{ fontSize: 11, fill: "#94a3b8" }} width={55} />
            <Tooltip formatter={(v: number) => money(v)} contentStyle={{ fontSize: 12, borderRadius: 0 }} />
            <Legend wrapperStyle={{ fontSize: 11 }} />
            <Bar dataKey={data.previous_label} fill="#94a3b8" radius={[2, 2, 0, 0]} />
            <Bar dataKey={data.current_label} fill="#DD0000" radius={[2, 2, 0, 0]} />
          </BarChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
};

export default ReportsPage;
